import { FilterOptionsState } from '@mui/material';
import {
  AutocompleteOption,
  NoSuggestionsOption,
  ShowMoreOption,
  SuggestionsLabelOption
} from './FavoriteAutocomplete.types';

export function getLabelProp(optionLabelProp: string | string[]): string {
  return typeof optionLabelProp === 'string'
    ? optionLabelProp
    : Array.isArray(optionLabelProp) && optionLabelProp.length > 0
    ? optionLabelProp[0]
    : '';
}

export function isShowMoreOption<T>(
  option: ShowMoreOption | T | undefined,
  showMoreId: string
): boolean {
  return option?.['id'] === showMoreId;
}

export function isNoSuggestionsOption<T>(
  option: NoSuggestionsOption | T | undefined,
  noSuggestionsId: string
): boolean {
  return option?.['id'] === noSuggestionsId;
}

export function isSuggestionsLabelOption<T>(
  option: SuggestionsLabelOption | T | undefined,
  suggestionsLabelId: string
): boolean {
  return option?.['id'] === suggestionsLabelId;
}

export function preFilterOptions<T>(
  options: T[],
  suggestedOptions: T[],
  filterOnFavorite: boolean,
  entityIdProperty: string,
  idProperty: string,
  noSuggestionsOption: NoSuggestionsOption,
  suggestionsLabelOption: SuggestionsLabelOption,
  showMoreOption: ShowMoreOption,
  showMoreButton: boolean = true
): AutocompleteOption<T>[] {
  // If we're in "show all" mode, return all options directly
  if (!filterOnFavorite) {
    return options;
  }

  // If we have suggested options to show, display them with optional "show more" button
  if (suggestedOptions.length > 0 || showMoreButton) {
    // Get IDs to check for duplicates
    const getId = (option: T) => option[entityIdProperty] || option[idProperty];
    const optionIds = new Set(options.map(getId));

    // Filter out suggested activities that are already in options
    const uniqueSuggested = suggestedOptions.filter(
      (suggested) => !optionIds.has(getId(suggested))
    );

    const result: AutocompleteOption<T>[] = [];

    if (uniqueSuggested.length === 0) {
      // Show "no suggestions" message if all suggested items were duplicates
      result.push(noSuggestionsOption);
    } else {
      // Show "suggestions" label at the top
      result.push(suggestionsLabelOption);
      // Show the unique suggested activities
      result.push(...uniqueSuggested);
    }

    // Conditionally show "Show More" button based on grant/permission
    if (showMoreButton) {
      result.push(showMoreOption);
    }

    return result;
  }

  // Default: show all options
  return options;
}

export function sortOptions<T>(
  options: AutocompleteOption<T>[],
  isFavoriteProp: string,
  sortBy: string,
  disableSorting: boolean | undefined,
  isShowMore: (o: AutocompleteOption<T> | null | undefined) => boolean,
  isNoSuggestion: (o: AutocompleteOption<T> | null | undefined) => boolean,
  isSuggestionsLabel: (o: AutocompleteOption<T> | null | undefined) => boolean
): AutocompleteOption<T>[] {
  // Separate special options from regular options
  const suggestionsLabel = options.filter(isSuggestionsLabel);
  const noSuggestions = options.filter(isNoSuggestion);
  const showMore = options.filter(isShowMore);
  const regularOptions = options.filter(
    (o) => !isShowMore(o) && !isNoSuggestion(o) && !isSuggestionsLabel(o)
  ) as T[];

  // Sort regular options: favorites first, then alphabetically
  const sorted = disableSorting
    ? regularOptions.sort(
        (a, b) => Number(b[isFavoriteProp]) - Number(a[isFavoriteProp])
      )
    : regularOptions.sort(
        (a, b) =>
          Number(b[isFavoriteProp]) - Number(a[isFavoriteProp]) ||
          a[sortBy]?.localeCompare(b[sortBy])
      );

  // Order: "suggestions" label, regular options, "no suggestions", "show more"
  return [...suggestionsLabel, ...sorted, ...noSuggestions, ...showMore];
}

export function createFilterOptions<T>(
  labelProps: string[],
  isShowMore: (o: AutocompleteOption<T> | null | undefined) => boolean,
  isNoSuggestion: (o: AutocompleteOption<T> | null | undefined) => boolean,
  isSuggestionsLabel: (o: AutocompleteOption<T> | null | undefined) => boolean
) {
  return (
    options: AutocompleteOption<T>[],
    state: FilterOptionsState<AutocompleteOption<T>>
  ) => {
    return [
      ...options.filter(isSuggestionsLabel),
      ...options.filter(
        (option) =>
          !isShowMore(option) &&
          !isNoSuggestion(option) &&
          !isSuggestionsLabel(option) &&
          (!state.inputValue ||
            state.inputValue === '' ||
            labelProps.some(
              (prop) =>
                (option[prop] as string)
                  ?.toLowerCase()
                  .indexOf(state.inputValue.toLowerCase()) !== -1
            ))
      ),
      ...options.filter(isNoSuggestion),
      ...options.filter(isShowMore)
    ];
  };
}
